import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Users, Package, Tag, Mail, ArrowRight, X } from 'lucide-react';
import StatusBadge from './StatusBadge';

export const GlobalSearchModal = ({ isOpen, onClose, orders = [], products = [], customers = [], coupons = [], messages = [] }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Perform search across all entities
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { orders: [], products: [], customers: [], coupons: [], messages: [] };

    const matchedOrders = orders.filter(o =>
      (o.orderNumber && o.orderNumber.toLowerCase().includes(q)) ||
      (o.email && o.email.toLowerCase().includes(q)) ||
      (o.shippingAddress?.fullName && o.shippingAddress.fullName.toLowerCase().includes(q)) ||
      (o.shippingAddress?.city && o.shippingAddress.city.toLowerCase().includes(q)) ||
      (o.shippingAddress?.postalCode && o.shippingAddress.postalCode.toLowerCase().includes(q))
    ).slice(0, 4);

    const matchedProducts = products.filter(p =>
      (p.title && p.title.toLowerCase().includes(q)) ||
      (p.articleCode && p.articleCode.toLowerCase().includes(q)) ||
      (p.category && p.category.toLowerCase().includes(q))
    ).slice(0, 4);

    const matchedCustomers = customers.filter(c =>
      (c.firstName && c.firstName.toLowerCase().includes(q)) ||
      (c.lastName && c.lastName.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      (c.phoneNumber && c.phoneNumber.includes(q))
    ).slice(0, 3);

    const matchedCoupons = coupons.filter(cp =>
      (cp.code && cp.code.toLowerCase().includes(q)) ||
      (cp.reference && cp.reference.toLowerCase().includes(q))
    ).slice(0, 3);

    const matchedMessages = messages.filter(m =>
      (m.name && m.name.toLowerCase().includes(q)) ||
      (m.email && m.email.toLowerCase().includes(q)) ||
      (m.subject && m.subject.toLowerCase().includes(q))
    ).slice(0, 3);

    return {
      orders: matchedOrders,
      products: matchedProducts,
      customers: matchedCustomers,
      coupons: matchedCoupons,
      messages: matchedMessages,
    };
  }, [query, orders, products, customers, coupons, messages]);

  const totalResultsCount =
    results.orders.length +
    results.products.length +
    results.customers.length +
    results.coupons.length +
    results.messages.length;

  if (!isOpen) return null;

  const handleSelect = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-stone-900/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header Input */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-stone-200">
          <Search className="w-5 h-5 text-stone-400 mr-3 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search orders (ID, customer, postcode), products, SKUs, customers..."
            className="w-full text-stone-900 placeholder-stone-400 bg-transparent text-sm focus:outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-stone-100 rounded-md text-stone-400 mr-2"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <span className="text-[11px] font-medium text-stone-400 bg-stone-100 px-2 py-0.5 rounded border border-stone-200 shrink-0">
            ESC to close
          </span>
        </div>

        {/* Results Body */}
        <div className="overflow-y-auto p-3 space-y-4 text-xs">
          {!query.trim() ? (
            <div className="p-8 text-center text-stone-400">
              <Search className="w-8 h-8 mx-auto mb-2 text-stone-300 stroke-[1.5]" />
              <p className="font-medium text-stone-600">Quick Navigation & Search</p>
              <p className="text-[11px] mt-1 text-stone-400">
                Type an Order ID (e.g. ORD-...), SKU, Customer Name, Email, or UK Postcode.
              </p>
            </div>
          ) : totalResultsCount === 0 ? (
            <div className="p-8 text-center text-stone-400">
              <p className="text-stone-600 font-medium">No results found for &ldquo;{query}&rdquo;</p>
              <p className="text-[11px] mt-1 text-stone-400">
                Check for typos or try searching with an order ID, email, or article code.
              </p>
            </div>
          ) : (
            <>
              {/* Orders Section */}
              {results.orders.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1.5 font-semibold text-stone-400 text-[11px] uppercase tracking-wider">
                    <ShoppingBag className="w-3.5 h-3.5" /> Orders ({results.orders.length})
                  </div>
                  <div className="space-y-1">
                    {results.orders.map(order => (
                      <div
                        key={order._id}
                        onClick={() => handleSelect(`/order/${order._id}`)}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-amber-50/60 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="font-mono font-semibold text-stone-900 group-hover:text-amber-800">
                            {order.orderNumber || order._id.slice(-8)}
                          </div>
                          <div className="text-stone-500">
                            {order.shippingAddress?.fullName || order.email}
                          </div>
                          <div className="text-stone-400 text-[11px]">
                            {order.shippingAddress?.city} ({order.shippingAddress?.postalCode})
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-stone-900">
                            £{(order.pricing?.totalAmount || order.totalAmount || 0).toFixed(2)}
                          </span>
                          <StatusBadge type="order" status={order.status} />
                          <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Products Section */}
              {results.products.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1.5 font-semibold text-stone-400 text-[11px] uppercase tracking-wider">
                    <Package className="w-3.5 h-3.5" /> Products ({results.products.length})
                  </div>
                  <div className="space-y-1">
                    {results.products.map(prod => (
                      <div
                        key={prod._id}
                        onClick={() => handleSelect(`/view-products`)}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-amber-50/60 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          {prod.image?.[0]?.src ? (
                            <img src={prod.image[0].src} alt="" className="w-7 h-7 object-cover rounded border border-stone-200" />
                          ) : (
                            <div className="w-7 h-7 bg-stone-100 rounded flex items-center justify-center text-stone-400">
                              <Package className="w-3.5 h-3.5" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-stone-900 group-hover:text-amber-800">{prod.title}</div>
                            <div className="text-[11px] text-stone-400 font-mono">SKU: {prod.articleCode} • {prod.category}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-stone-900">
                            {prod.numericPrice ? `£${prod.numericPrice}` : prod.price}
                          </span>
                          <StatusBadge type="inventory" status={prod.inventory <= 0 ? 'out_of_stock' : prod.inventory < 10 ? 'low_stock' : 'healthy'} />
                          <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Customers Section */}
              {results.customers.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1.5 font-semibold text-stone-400 text-[11px] uppercase tracking-wider">
                    <Users className="w-3.5 h-3.5" /> Customers ({results.customers.length})
                  </div>
                  <div className="space-y-1">
                    {results.customers.map(cust => (
                      <div
                        key={cust._id}
                        onClick={() => handleSelect(`/customers`)}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-amber-50/60 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 bg-stone-100 rounded-full flex items-center justify-center font-bold text-[11px] text-stone-700">
                            {(cust.firstName?.[0] || 'C').toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-stone-900 group-hover:text-amber-800">
                              {cust.firstName} {cust.lastName}
                            </div>
                            <div className="text-[11px] text-stone-400">{cust.email} • {cust.phoneNumber || 'No phone'}</div>
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coupons Section */}
              {results.coupons.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1.5 font-semibold text-stone-400 text-[11px] uppercase tracking-wider">
                    <Tag className="w-3.5 h-3.5" /> Coupons ({results.coupons.length})
                  </div>
                  <div className="space-y-1">
                    {results.coupons.map(cpn => (
                      <div
                        key={cpn._id}
                        onClick={() => handleSelect(`/coupons`)}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-amber-50/60 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="px-2 py-0.5 bg-stone-100 border border-stone-200 font-mono font-bold text-stone-900 rounded">
                            {cpn.code}
                          </span>
                          <span className="text-stone-500">
                            {cpn.discountPercentage}% off • {cpn.reference}
                          </span>
                        </div>
                        <span className="text-stone-400 text-[11px]">
                          Used {cpn.usage?.totalUses || 0} times
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages Section */}
              {results.messages.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-2 mb-1.5 font-semibold text-stone-400 text-[11px] uppercase tracking-wider">
                    <Mail className="w-3.5 h-3.5" /> Support Messages ({results.messages.length})
                  </div>
                  <div className="space-y-1">
                    {results.messages.map(msg => (
                      <div
                        key={msg._id}
                        onClick={() => handleSelect(`/contacts`)}
                        className="flex items-center justify-between p-2.5 rounded-lg hover:bg-amber-50/60 cursor-pointer transition-colors group"
                      >
                        <div>
                          <div className="font-medium text-stone-900 group-hover:text-amber-800">{msg.subject}</div>
                          <div className="text-[11px] text-stone-400">From {msg.name} ({msg.email})</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-stone-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;
